// ScientificMultiplyTask.js
// Mnożenie: (a1·10^k1) · (a2·10^k2)

import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import FractionInput from "../../../../components/inputs/FractionInput";
import { normalizeUserMathToLatex, parseScientificString } from "../../../../utils/formatUserMath";

const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];
const int = (a, b) => a + Math.floor(Math.random() * (b - a + 1));

function randMantissa() {
  const dec = pick([0, 1, 2]);
  if (dec === 0) {
    const v = int(1, 9);
    return { value: v, str: String(v) };
  }
  if (dec === 1) {
    const a = int(1, 9), b = int(1, 9);
    const v = parseFloat(`${a}.${b}`);
    return { value: v, str: v.toFixed(1) };
  }
  const a = int(1, 9), b = int(0, 9), c = int(1, 9);
  const v = parseFloat(`${a}.${b}${c}`);
  return { value: v, str: v.toFixed(2) };
}
function randNonZeroExp() { return pick([-3, -2, -1, 1, 2, 3]); }

const fmt = (x) => String(x).replace(".", ",");
const canonSci = (aStr, k) => `${aStr.replace(".", ",")}\\,\\cdot\\,10^{${k}}`;

function sig5_noRound(m) {
  const s = m.toString();
  const digits = s.replace(".", "");
  const take = Math.min(5, digits.length);
  const first = digits[0];
  const rest = digits.slice(1, take);
  let out = first + (rest ? "." + rest : "");
  out = out.replace(/0+$/, "").replace(/\.$/, "");
  return { str: out, num: Number(out) };
}

export function generateTask() {
  const m1 = randMantissa(), m2 = randMantissa();
  const k1 = randNonZeroExp(), k2 = randNonZeroExp();
  const leftLatex = `${canonSci(m1.str, k1)}\\,\\cdot\\,${canonSci(m2.str, k2)}`;

  let mantissa = m1.value * m2.value;
  let k = k1 + k2;
  while (mantissa >= 10) { mantissa /= 10; k += 1; }
  while (mantissa < 1) { mantissa *= 10; k -= 1; }

  const { str: mantissaStr, num: mantissaNum } = sig5_noRound(mantissa);

  return {
    type: "ScientificMultiplyTask",
    data: { leftLatex, m1, k1, m2, k2, mantissa: mantissaNum, mantissaStr, k }
  };
}

export function renderTask({ data }) {
  return (
    <div style={{ marginBottom: "12px" }}>
      Oblicz wynik i zapisz w <strong>notacji wykładniczej</strong>:{" "}
      <InlineMath math={data.leftLatex} />
    </div>
  );
}

export function renderInput(task, user, onChange, disabled) {
  const handleChange = (ans) => onChange?.(ans);

  // STAŁY placeholder — nie zdradza wyniku zadania
  const placeholder=`np. 7,7·10^7 lub 7,7e7`;

  return (
    <div>
      <FractionInput
        value={user}
        onChange={handleChange}
        disabled={disabled}
        placeholder={placeholder}
        label=""
        powerPreview={false}
        sciPreview={true}   // podgląd notacji wykładniczej w polu
      />
      <div aria-hidden style={{ marginTop: "-16px" }} />
      <div aria-hidden style={{ height: "12px" }} />
    </div>
  );
}

export function checkAnswer({ data }, userAnswer) {
  if (!userAnswer?.raw) return false;

  const parsed = parseScientificString(userAnswer.raw);
  if (!parsed) return false;

  const { mantissa, exponent } = parsed;
  if (!(mantissa >= 1 && mantissa < 10)) return false;
  if (exponent !== data.k) return false;

  return Math.abs(mantissa - data.mantissa) < 1e-10;
}

export function renderUserAnswer(ans) {
  if (!ans?.raw) return <i>(brak odpowiedzi)</i>;
  return <InlineMath math={normalizeUserMathToLatex(ans.raw)} />;
}

export function renderAnswer({ data }) {
  return <InlineMath math={`${fmt(data.mantissaStr)}\\,\\cdot\\,10^{${data.k}}`} />;
}

export function renderExplanation({ data }) {
  const { m1, m2, k1, k2, mantissaStr, k } = data;
  const m1s = m1.str.replace(".", ","), m2s = m2.str.replace(".", ",");
  const stepStr = (m1.value * m2.value).toString().replace(".", ",");
  return (
    <div style={{ display: "grid", rowGap: "8px" }}>
      <p><strong>Krok 1:</strong> Zapisz działanie:</p>
      <BlockMath math={`(${m1s}\\,\\cdot\\,10^{${k1}})\\,\\cdot\\,(${m2s}\\,\\cdot\\,10^{${k2}})`} />

      <p><strong>Krok 2:</strong> Pomnóż mantysy i dodaj wykładniki potęg dziesięciu:</p>
      <BlockMath math={`(${m1s}\\,\\cdot\\,${m2s})\\,\\cdot\\,10^{${k1}+${k2}}`} />
      <BlockMath math={`=\\;${stepStr}\\,\\cdot\\,10^{${k1 + k2}}`} />

      <p><strong>Krok 3:</strong> Znormalizuj mantysę do [1,10) i utnij do ≤5 cyfr znaczących:</p>
      <BlockMath math={`${fmt(mantissaStr)}\\,\\cdot\\,10^{${k}}`} />
    </div>
  );
}
